import { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

/**
 * Modal for viewing, searching, and selecting reports to assign to a security group.
 * Combine current group reports and available reports for the selection list.
 * - show {boolean}: Whether the modal is visible.
 * - onHide {() => void}: Callback to close the modal.
 * - groupReports {Array<Object>}: Reports currently assigned to the group.
 * - availableReports {Array<Object>}: Reports available to be assigned.
 * - selectedReports {number[]}: Array of selected report IDs.
 * - onSelectionChange {(ids: number[]) => void}: Setter for selectedReports.
 * - onSave {(ids: number[]) => Promise<void> | void}: Called when saving the current selection.
 * @returns {JSX.Element} A modal with a selectable reports table and actions.
 */
const ReportSelectionModal = ({
  show,
  onHide,
  groupReports,
  availableReports,
  selectedReports,
  onSelectionChange,
  onSave
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allReports = [
    ...groupReports.map(report => ({ ...report, isInGroup: true })),
    ...availableReports.map(report => ({ ...report, isInGroup: false }))
  ];

  const filteredReports = allReports.filter(report => {
    const searchLower = searchTerm.toLowerCase();
    return (
      report.ReportName?.toLowerCase().includes(searchLower) ||
      report.ReportCode?.toLowerCase().includes(searchLower) ||
      report.ReportDescription?.toLowerCase().includes(searchLower) ||
      report.WorkspaceName?.toLowerCase().includes(searchLower) ||
      report.ReportType?.toLowerCase().includes(searchLower)
    );
  });

  /**
   * Toggles a single report selection on/off.
   * @param {number} reportId - The unique ID of the report.
   * @param {boolean} checked - Whether the report should be selected.
   * @returns {void}
   */
  const handleReportToggle = (reportId, checked) => {
    if (checked) {
      onSelectionChange([...selectedReports, reportId]);
    } else {
      onSelectionChange(selectedReports.filter(id => id !== reportId));
    }
  };

  /**
   * Selects or clears all currently filtered reports.
   * @param {boolean} checked - If true, selects all filtered reports; otherwise clears selection.
   * @returns {void}
   */
  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(filteredReports.map(report => report.ReportID));
    } else {
      onSelectionChange([]);
    }
  };

  /**
   * Persists the current selection by invoking onSave.
   * Shows a submitting state while the save is in progress.
   * @returns {Promise<void>}
   */
  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await onSave(selectedReports);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Renders a checkbox cell for selecting an individual report row.
   * @param {Object} rowData - The row object for the report.
   * @returns {JSX.Element} A checkbox reflecting the row's selection state.
   */
  const checkboxTemplate = (rowData) => {
    const isSelected = selectedReports.includes(rowData.ReportID);
    return (
      <Form.Check
        type="checkbox"
        checked={isSelected}
        onChange={(e) => handleReportToggle(rowData.ReportID, e.target.checked)}
      />
    );
  };

  /**
   * Renders a status badge indicating if the report is already in the group.
   * @param {Object} rowData - The row object for the report.
   * @returns {JSX.Element} Badge showing "Current Access" or "Available".
   */
  const statusTemplate = (rowData) => {
    return rowData.isInGroup ? (
      <span className="badge bg-primary">Current Access</span>
    ) : (
      <span className="badge bg-secondary">Available</span>
    );
  };

  /**
   * Renders a badge for the report type (e.g., Report vs. other types).
   * @param {Object} rowData - The row object for the report.
   * @returns {JSX.Element} A colored badge indicating report type.
   */
  const typeTemplate = (rowData) => {
    return (
      <span className={`badge ${rowData.ReportType === 'Report' ? 'bg-info' : 'bg-warning'}`}>
        {rowData.ReportType}
      </span>
    );
  };

  const allSelected = filteredReports.length > 0 && filteredReports.every(report => selectedReports.includes(report.ReportID));
  const someSelected = filteredReports.some(report => selectedReports.includes(report.ReportID));

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Manage Group Reports</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <Form.Check
            type="checkbox"
            label={`Select All (${filteredReports.length} reports)`}
            checked={allSelected}
            ref={input => {
              if (input) input.indeterminate = someSelected && !allSelected;
            }}
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
        </div>

        <Alert variant="info" className="small">
          <strong>Selected:</strong> {selectedReports.length} reports will be accessible to this group.
        </Alert>

        <div className="datatable-custom">
          <div className="m-0 justify-content-end d-flex align-items-end p-3">
            <input
              type="text"
              className="m-0 col-2 card d-flex align-items-end p-2"
              placeholder="Type to Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="p-inputgroup-addon">
              <i className="pi pi-search"></i>
            </span>
          </div>
          <div style={{ height: '400px', overflow: 'auto' }}>
            <DataTable
              value={filteredReports}
              removableSort
              tableClassName="table dataTable no-footer table-striped"
              emptyMessage="No reports found"
              scrollable
              scrollHeight="100%"
            >
            <Column
              body={checkboxTemplate}
              header=""
              style={{ width: '50px' }}
            />
            <Column
              field="ReportName"
              header="Report Name"
              sortable
            />
            <Column
              field="ReportCode"
              header="Code"
              sortable
              style={{ width: '100px' }}
            />
            <Column
              field="ReportDescription"
              header="Description"
              sortable
            />
            <Column
              field="WorkspaceName"
              header="Workspace"
              sortable
              style={{ width: '150px' }}
            />
            <Column
              body={typeTemplate}
              header="Type"
              style={{ width: '120px' }}
            />
            <Column
              body={statusTemplate}
              header="Status"
              style={{ width: '130px' }}
            />
            </DataTable>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : `Save (${selectedReports.length} reports)`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReportSelectionModal;
